/**
 * @fileoverview Shared Database Error screen used by tenant-web, system-web,
 *               and mobile-pwa after localDatabaseManager reports status=error.
 * @module local-db/components/local-database-error-screen
 *
 * Responsibilities:
 * - Render the blocking error overlay with Retry and Reset Local Data actions.
 * - Retry strips `__fs_update` so the loop-guard does not instantly re-fail.
 * - Reset deletes the host Dexie database(s) after user confirmation.
 *
 * Dependencies:
 * - react
 * - ../services/localDbErrorRecovery
 *
 * Architecture:
 * - Presentation + recovery actions only. Hosts pass copy and dbNames; they
 *   must not reimplement URL surgery or Dexie.delete.
 *
 * Notes:
 * - Data Source: none (error overlay; no Dexie reads).
 */

import { retryWithoutUpdateParam, resetLocalDatabaseAndReload } from '../services/localDbErrorRecovery';

/**
 * @component LocalDatabaseErrorScreen
 *
 * Purpose:
 * - Blocking overlay when the local database cannot open after recovery.
 *
 * Props:
 * - title: Heading text.
 * - description: User-facing explanation.
 * - error: Optional technical message from the manager.
 * - dbNames: IndexedDB names to delete on Reset.
 *
 * Behavior:
 * - Retry navigates without `__fs_update`.
 * - Reset Local Data confirms, wipes named DBs, then navigates the same way.
 *
 * Data Source: none (local error UI only).
 * Constraints: Full-screen overlay; no writes except the optional reset path.
 * Performance: Static markup; no subscriptions.
 */
export function LocalDatabaseErrorScreen({
  title = 'Database Error',
  description = 'The local database could not be initialized.',
  error,
  dbNames,
}: {
  title?: string;
  description?: string;
  error?: string | null;
  dbNames: readonly string[];
}) {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-slate-900 px-6">
      <div className="text-center p-8 max-w-sm">
        <div className="text-red-400 text-4xl mb-4">⚠</div>
        <p className="text-white font-semibold mb-2">{title}</p>
        <p className="text-slate-400 text-sm mb-4">{description}</p>
        {error && (
          <p className="text-xs text-slate-500 font-mono mb-6 break-all">{error}</p>
        )}
        <div className="flex flex-col gap-3 items-center">
          <button
            type="button"
            onClick={() => retryWithoutUpdateParam()}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700"
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => { void resetLocalDatabaseAndReload(dbNames); }}
            className="px-4 py-2 text-sm font-medium text-slate-300 border border-slate-600 rounded-lg hover:border-slate-400"
          >
            Reset Local Data & Reload
          </button>
        </div>
      </div>
    </div>
  );
}
